<script setup lang="ts">
import { CalendarIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import { useNotifications } from '~/composables/useStates/useNotifications';
import NotificationsNotificationContextMenu from '~/components/marketing/notifications/notifications-notification-context-menu.vue';
import { type Dayjs } from 'dayjs';
import Button from '~/components/design-system/button.vue';

const { $trackEvent, $dayjs } = useNuxtApp();

const {
  activeTab,
  availableNotifications,
  fetchNotifications,
  notificationsState,
  resetNotificationState,
  updateByIds,
} = useNotifications();

const rightClicked = ref<boolean>(false);
const range = ref<{ startDate: Dayjs; endDate: Dayjs }>();
const disableConfirm = ref<boolean>(true);
const calendarOpen = ref<boolean>(false);

const currentNotifications = computed(() =>
  activeTab.value === 'All'
    ? notificationsState.value.allNotifications.notifications
    : activeTab.value === 'Marketing'
      ? notificationsState.value.marketingNotifications.notifications
      : activeTab.value === 'Transactional'
        ? notificationsState.value.transactionalNotifications.notifications
        : notificationsState.value.operationalNotifications.notifications,
);

const hasUnread = computed(() =>
  currentNotifications.value.some((n) => !n.isRead),
);
const contextMenuOptions = computed(() => {
  const ids = currentNotifications.value
    .filter((n) => !n.isGlobal)
    .map((n) => n.notificationId);
  const markAsText = `mark-${activeTab.value.toLowerCase()}-messages-as-${hasUnread.value ? 'read' : 'unread'}`;

  return [
    {
      label: markAsText,
      action: async () => {
        $trackEvent(
          'Notification',
          hasUnread.value
            ? 'NOTIFICATION_MARK_ALL_AS_UNREAD'
            : 'NOTIFICATION_MARK_ALL_AS_READ',
          { notificationCategory: activeTab.value, ids },
        );
        updateByIds({
          isRead: hasUnread.value,
          ids,
          updateType: 'Status',
        });
      },
      requiresConfirm: true,
      confirmKeys: {
        titleKey: `confirm-${markAsText}`,
        confirmKey: 'confirm',
        cancelKey: 'cancel',
      },
    },
    {
      label: `delete-${activeTab.value.toLowerCase()}-messages`,
      action: async () => {
        $trackEvent('Notification', 'NOTIFICATION_DELETE_ALL', {
          notificationCategory: activeTab.value,
          ids,
        });
        updateByIds({
          ids,
          updateType: 'Delete',
        });
      },
      requiresConfirm: true,
      confirmKeys: {
        titleKey: 'delete-all-notifications-confirm',
        confirmKey: 'delete-all',
        cancelKey: 'cancel',
      },
    },
  ];
});

const handleConfirm = () => {
  if ('startDate' in range.value && 'endDate' in range.value) {
    const { startDate, endDate } = range.value;
    notificationsState.value = {
      ...notificationsState.value,
      hasDateFilter: true,
      startDate: startDate.utc(true).format(),
      endDate: endDate.utc(true).format(),
      skip: 0,
      page: 1,
      limit: 20,
    };
    fetchNotifications({
      skip: notificationsState.value.skip,
      startDate: notificationsState.value.startDate,
      endDate: notificationsState.value.endDate,
      limit: notificationsState.value.limit,
      overwriteStorage: true,
    });
    $trackEvent('Notification', 'NOTIFICATION_PANEL_FILTER_BY_DATE', {
      skip: notificationsState.value.skip,
      startDate: notificationsState.value.startDate,
      endDate: notificationsState.value.endDate,
      limit: notificationsState.value.limit,
    });
    disableConfirm.value = true;
    calendarOpen.value = false;
  }
};

const handleReset = () => {
  disableConfirm.value = true;
  resetNotificationState(true);
  calendarOpen.value = false;
  $trackEvent('Notification', 'NOTIFICATION_PANEL_RESET_DATE_FILTER');
};
const handleCalendarToggle = () => {
  if (calendarOpen.value) {
    calendarOpen.value = false;
    if (notificationsState.value.hasDateFilter) handleReset();
    range.value = undefined;
    $trackEvent('Notification', 'NOTIFICATION_PANEL_CLOSE_DATE_PICKER');
  } else {
    range.value = undefined;
    calendarOpen.value = true;
    $trackEvent('Notification', 'NOTIFICATION_PANEL_OPEN_DATE_PICKER');
  }
};

const handleDateRangeClick = (
  rangeType: 'last-week' | 'last-month' | 'last-3-months',
) => {
  notificationsState.value.hasDateFilter = true;
  const today = $dayjs();
  switch (rangeType) {
    case 'last-week':
      range.value = {
        startDate: today.subtract(7, 'day').utc(true),
        endDate: today.utc(true),
      };
      break;
    case 'last-month':
      range.value = {
        startDate: today.subtract(1, 'month').utc(true),
        endDate: today.utc(true),
      };
      break;
    case 'last-3-months':
      range.value = {
        startDate: today.subtract(3, 'month').utc(true),
        endDate: today.utc(true),
      };
      break;
  }
  handleConfirm();
};
</script>
<template>
  <div
    class="flex flex-col gap-2 justify-between items-center sticky z-10 shadow-lg bg-layer-secondary w-full"
    :class="{
      'top-16': availableNotifications.length < 1,
      'top-28': availableNotifications.length >= 1,
    }"
  >
    <div
      class="bg-base flex justify-between items-center w-full px-4 py-2"
      @contextmenu.prevent="rightClicked = true"
    >
      <DesignSystemButton
        element-name="notifications-panel-date-picker"
        :type="
          calendarOpen && !notificationsState.hasDateFilter
            ? 'primary'
            : 'tertiary'
        "
        class="relative"
        size="medium"
        rounding="md"
        aria-label="Calendar"
        @click="handleCalendarToggle"
      >
        <CalendarIcon />
        <div
          v-if="notificationsState.hasDateFilter"
          class="absolute top-2 right-1 w-2 h-2 bg-primary-blue-gradient rounded-full"
        />
      </DesignSystemButton>

      <NotificationsNotificationContextMenu
        v-if="
          availableNotifications.some((tab) => tab.notifications.length > 0)
        "
        v-model="rightClicked"
        :menu-options="contextMenuOptions"
        class="mr-2"
      />
    </div>
    <Transition name="scale">
      <div v-if="calendarOpen" class="w-full flex flex-col gap-3 px-4 pb-2">
        <div class="flex justify-between items-center bg-base p-2 rounded-t-lg">
          <span class="text-base-priority font-bold capitalize">
            {{ $t('select-a-date-range') }}
          </span>
          <Button
            element-name="close-notification-panel-date-picker"
            type="tertiary"
            size="medium"
            rounding="md"
            padding="sm"
            aria-label="Close"
            @click="handleCalendarToggle"
          >
            <XMarkIcon />
          </Button>
        </div>
        <div class="px-2 w-full flex flex-col gap-3">
          <DesignSystemInputsDatePicker
            v-if="calendarOpen"
            v-model:date="range"
            mode="range"
            :min-date="notificationsState.maxDate"
            :max-date="notificationsState.minDate"
            :close-on-select="false"
            :hide-reset-over-ride="true"
            @confirm="disableConfirm = false"
            @reset="handleReset"
          />
          <div class="flex justify-between gap-2">
            <DesignSystemButton
              type="secondary"
              padding="wide-short"
              rounding="md"
              class="text-xxs w-full justify-center items-center"
              @click="handleDateRangeClick('last-week')"
            >
              {{ $t('last-week') }}
            </DesignSystemButton>
            <DesignSystemButton
              type="secondary"
              padding="wide-short"
              rounding="md"
              class="text-xxs w-full justify-center items-center"
              @click="handleDateRangeClick('last-month')"
            >
              {{ $t('last-month') }}
            </DesignSystemButton>
            <DesignSystemButton
              type="secondary"
              padding="wide-short"
              rounding="md"
              class="text-xxs w-full justify-center items-center"
              @click="handleDateRangeClick('last-3-months')"
            >
              {{ $t('last-3-months') }}
            </DesignSystemButton>
          </div>
        </div>

        <div class="flex justify-between items-center bg-base p-2 rounded-b-lg">
          <Button
            element-name="confirm-dates-notification-panel-date-picker"
            type="primary"
            rounding="lg"
            padding="md"
            class="w-full justify-center items-center capitalize"
            aria-label="Confirm"
            :disabled="disableConfirm"
            @click="handleConfirm"
          >
            {{ $t('confirm-dates') }}
          </Button>
        </div>
      </div>
    </Transition>
  </div>
</template>
